import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { loadMyLinksStats, type MyLinksStats } from '@/lib/mylinksStats';

interface MyLinksSummaryCardProps {
  merchantId: number | null;
}

/**
 * Carte de synthèse MyLinks sur l'accueil du dashboard :
 * vues, clics sur les offres, taux de clic et meilleure source.
 */
export default function MyLinksSummaryCard({ merchantId }: MyLinksSummaryCardProps) {
  const [stats, setStats] = useState<MyLinksStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!merchantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await loadMyLinksStats(merchantId, 30);
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Statistiques indisponibles');
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    load();
  }, [load]);

  const maxDaily = Math.max(1, ...(stats?.daily.map((d) => d.count) || [1]));
  const topSource = stats?.sources[0];

  const kpis = [
    { label: 'Vues 7 jours', value: stats?.weekViews ?? 0, icon: 'ri-eye-line' },
    { label: 'Vues 30 jours', value: stats?.totalViews ?? 0, icon: 'ri-bar-chart-line' },
    { label: 'Clics offres', value: stats?.totalClicks ?? 0, icon: 'ri-cursor-line' },
  ];

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 mb-6 md:mb-8">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-links-line text-sm"></i>
          </span>
          <div>
            <h3 className="text-sm font-semibold text-foreground-800">Statistiques MyLinks</h3>
            <p className="text-[11px] text-foreground-500">Trafic reçu sur votre lien unique</p>
          </div>
        </div>
        <Link
          to="/dashboard/mylinks-stats"
          className="text-xs text-foreground-500 hover:text-foreground-800 flex items-center gap-1 whitespace-nowrap transition-colors cursor-pointer"
        >
          Détail complet<i className="ri-arrow-right-line"></i>
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : error ? (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 py-4">
          <p className="text-xs text-foreground-600 flex items-center gap-2">
            <i className="ri-error-warning-line text-red-400"></i>
            {error}
          </p>
          <button
            onClick={load}
            className="px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-[11px] font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Réessayer
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <div className="grid grid-cols-3 gap-2 md:gap-3">
              {kpis.map((kpi) => (
                <div key={kpi.label} className="rounded-lg bg-background-100 p-3">
                  <div className="flex items-center gap-1.5 mb-1.5 text-foreground-500">
                    <i className={`${kpi.icon} text-[11px]`}></i>
                    <span className="text-[10px] whitespace-nowrap">{kpi.label}</span>
                  </div>
                  <div className="text-lg md:text-xl font-bold font-heading text-foreground-950">
                    {kpi.value.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>

            {/* Mini graphe 14 jours */}
            <div className="mt-4">
              <div className="flex items-end gap-1 h-16">
                {(stats?.daily || []).map((day, i) => (
                  <div key={i} className="flex-1 flex flex-col justify-end group relative">
                    <div
                      className="w-full rounded-t-sm bg-accent-400/70 group-hover:bg-accent-500 transition-colors"
                      style={{ height: `${Math.max(4, Math.round((day.count / maxDaily) * 60))}px` }}
                    ></div>
                    <span className="absolute -top-6 left-1/2 -translate-x-1/2 hidden group-hover:block text-[9px] px-1.5 py-0.5 rounded bg-foreground-950 text-background-50 whitespace-nowrap">
                      {day.label} · {day.count}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-foreground-400 mt-1.5">Vues des 14 derniers jours</p>
            </div>
          </div>

          <div className="rounded-lg bg-accent-50/60 border border-accent-200/50 p-3 flex flex-col justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wide text-accent-700 font-semibold mb-1">Taux de clic</p>
              <p className="text-2xl font-bold font-heading text-foreground-950">{stats?.conversion ?? 0} %</p>
              <p className="text-[11px] text-foreground-500 mt-0.5">
                {stats?.totalClicks ?? 0} clic(s) pour {stats?.totalViews ?? 0} vue(s)
              </p>
            </div>
            <div className="mt-3 pt-3 border-t border-accent-200/50">
              <p className="text-[10px] uppercase tracking-wide text-accent-700 font-semibold mb-1">Meilleure source</p>
              <p className="text-xs font-medium text-foreground-800 capitalize">
                {topSource ? `${topSource.label} · ${topSource.count} vue(s)` : 'Aucune source pour le moment'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}